import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SKIP_CARD_PRODUCT_ID, Subscription } from '@demind-inc/core';

import { useAuthContext } from './AuthProvider';

import { useSubscriptions, useAvailableSubscriptions } from '../queries';
import { getRewardfulCampaign, getRewardfulCustomerId } from '../initializer';
import { trackEventMixpanel, useFeatureFlag } from '../../utils';
import { accountApi } from '../api';

interface ISubscriptionContext {
  currentSubscription?: Subscription;
  hasAccessToApp: boolean;
  isValidating: boolean;
  isRequestingSubscription: boolean;
  hasExpiredSubscription: boolean;
  rewardfulCaompaignId: string;
  rewardfulCustomerId: string;
  hasNoSubscriptionsTrial: boolean;
  hasSkipCardSubscription: boolean;
  /** Formatted lifetime plan price (e.g. "$120") from available Stripe prices, or undefined until loaded */
  lifetimePlanPrice: string | undefined;
  requestFreeSubscription: (onPurchaseDone: () => void) => Promise<void>;
  refetchSubscriptions: () => Promise<void>;
  requestSkipCardSubscription: (params: RequestSkipCardSubscriptionParams) => Promise<void>;
}

interface RequestSkipCardSubscriptionParams {
  trialDays: number;
  onPurchaseDone: () => void;
  discountId?: string;
  offeringId?: string;
}

export const SubscriptionContext = createContext({} as ISubscriptionContext);
export const useSubscriptionContext = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [rewardfulCaompaignId, setRewardfulCampaignId] = useState<string>('');
  const [rewardfulCustomerId, setRewardfulCustomerId] = useState<string>('');
  const [isRequestingSubscription, setIsRequestingSubscription] = useState(false);
  const { user, updateUser } = useAuthContext();
  const {
    subscriptions,
    isLoading: isFetchingSubscriptions,
    refetch,
  } = useSubscriptions({
    userId: user.userId,
  });

  const { availableSubscriptions } = useAvailableSubscriptions({
    provider: 'stripe',
    access: 'basic_price_up_202512',
    enabled: !!user.userId,
  });

  const lifetimePlanPrice = useMemo(() => {
    const lifetimePlan = availableSubscriptions?.find(
      (p) => p.lookup_key === 'basic_lifetime_prod' || p.lookup_key === 'basic_lifetime_staging'
    );
    if (!lifetimePlan?.unit_amount || lifetimePlan.currency == null) {
      return undefined;
    }
    return new Intl.NumberFormat('en-EN', {
      style: 'currency',
      currency: lifetimePlan.currency,
    }).format(lifetimePlan.unit_amount / 100);
  }, [availableSubscriptions]);

  useEffect(() => {
    window.addEventListener('Rewardful.initialized', function () {
      setRewardfulCampaignId(getRewardfulCampaign()?.id);
      setRewardfulCustomerId(getRewardfulCustomerId());
    });
  }, []);

  const refetchSubscriptions = async () => {
    await refetch();
  };

  const hasAccessToApp = useMemo(
    () => (subscriptions?.hasAccess === undefined ? true : subscriptions.hasAccess),
    [subscriptions]
  );

  const hasExpiredSubscription = useMemo(() => {
    const allSubscriptions = subscriptions?.subscriptions ?? [];
    if (!allSubscriptions.length) {
      return false;
    }
    return allSubscriptions.some(
      (sub) => sub.status === 'expired' && sub.productId !== SKIP_CARD_PRODUCT_ID
    );
  }, [subscriptions?.subscriptions]);

  const hasNoSubscriptionsTrial = useMemo(
    () => !subscriptions?.subscriptions?.length,
    [subscriptions?.subscriptions]
  );

  // If user has previously used a skip-card subscription, suppress free-trial offerings
  const hasSkipCardSubscription = useMemo(() => {
    const all = subscriptions?.subscriptions ?? [];
    return all.some((s) => s.productId === SKIP_CARD_PRODUCT_ID);
  }, [subscriptions?.subscriptions]);

  // If free plan applied, update user access to free
  const requestFreeSubscription = async (onPurchaseDone: () => void) => {
    setIsRequestingSubscription(true);
    try {
      await updateUser({
        access: 'free',
      });
      await refetchSubscriptions();
      onPurchaseDone();
      trackEventMixpanel('subscribe_free_plan', { from: 'web' });
    } finally {
      setIsRequestingSubscription(false);
    }
  };

  const requestSkipCardSubscription = async ({
    trialDays,
    onPurchaseDone,
    discountId,
    offeringId,
  }: RequestSkipCardSubscriptionParams) => {
    setIsRequestingSubscription(true);
    try {
      await accountApi.subscribeSkipCardSubscription({
        userId: user.userId,
        deviceFrom: 'web',
        trialDays,
        discountId,
        offeringId,
      });
      await refetchSubscriptions();
      onPurchaseDone();
    } finally {
      setIsRequestingSubscription(false);
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        currentSubscription: subscriptions?.activeSubscription,
        hasAccessToApp,
        isRequestingSubscription,
        isValidating: isFetchingSubscriptions,
        rewardfulCaompaignId,
        hasExpiredSubscription,
        hasNoSubscriptionsTrial,
        hasSkipCardSubscription,
        rewardfulCustomerId,
        lifetimePlanPrice,
        requestFreeSubscription,
        refetchSubscriptions,
        requestSkipCardSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
